import papa from 'papaparse'

import downloadCSV from '_lib/downloadCSV.js'

// ids sent per request — the API accepts up to 50, but smaller batches give
// finer progress and keep each request (one PDP import per audience) short
export const BATCH_SIZE = 10

// if this many whole batches in a row fail, the problem is systemic (expired
// session, PDP down, ...) — stop instead of failing every remaining batch
export const MAX_CONSECUTIVE_BATCH_FAILURES = 3

export const RESULT_STATUS = {
  PENDING: 'pending',
  MOVING: 'moving',
  MOVED: 'moved',
  SKIPPED: 'skipped',
  FAILED: 'failed',
}

// skip reasons returned by datasphere-api, plus 'stopped' for ids never sent
export const SKIP_REASONS = {
  ALREADY_IN_PDP: 'already_in_pdp',
  DELETED: 'deleted',
  TAXONOMY_AUDIENCE: 'taxonomy_audience',
  NOT_FOUND: 'not_found',
  STOPPED: 'stopped',
}

const SKIP_REASON_LABELS = {
  [SKIP_REASONS.ALREADY_IN_PDP]: 'Already in PDP',
  [SKIP_REASONS.DELETED]: 'Deleted',
  [SKIP_REASONS.TAXONOMY_AUDIENCE]: 'PDP taxonomy audience',
  [SKIP_REASONS.NOT_FOUND]: 'Not found',
  [SKIP_REASONS.STOPPED]: 'Stopped',
}

export const formatSkipReason = (reason) => SKIP_REASON_LABELS[reason] || reason

export const chunk = (items, size) => {
  const chunks = []
  for (let start = 0; start < items.length; start += size) {
    chunks.push(items.slice(start, start + size))
  }
  return chunks
}

/**
 * Why an audience can't be moved (mirrors the datasphere-api check, which would
 * skip it anyway), or null when it can. Field names are camelized.
 */
export const getSkipReason = (audience) => {
  if (audience.apa?.conversationId) return SKIP_REASONS.ALREADY_IN_PDP
  if (audience.status === 'deleted') return SKIP_REASONS.DELETED
  if (audience.kind === 'pdp_taxonomy') return SKIP_REASONS.TAXONOMY_AUDIENCE
  return null
}

const formatWarning = (warning) =>
  typeof warning === 'string' ? warning : warning?.message || JSON.stringify(warning)

/**
 * Maps one batch response onto the per-id results.
 * @param {object} results - { [id]: { status, reason, error, conversationId, warnings } }
 * @param {object} response - the camelized API response ({ moved, skipped, failed }),
 *   or { error } when the whole request failed
 * @param {string[]} batchIds - the ids sent in this batch
 * @returns {object} a new results object
 */
export function mergeBatchResult(results, response, batchIds) {
  const next = { ...results }
  const set = (id, result) => {
    next[id] = { warnings: [], ...result }
  }

  if (response?.error) {
    batchIds.forEach((id) => set(id, { status: RESULT_STATUS.FAILED, error: response.error }))
    return next
  }

  const answered = new Set()
  ;(response?.moved || []).forEach(({ id, conversationId, warnings }) => {
    answered.add(id)
    set(id, {
      status: RESULT_STATUS.MOVED,
      conversationId,
      warnings: (warnings || []).map(formatWarning),
    })
  })
  ;(response?.skipped || []).forEach(({ id, reason }) => {
    answered.add(id)
    set(id, { status: RESULT_STATUS.SKIPPED, reason })
  })
  ;(response?.failed || []).forEach(({ id, conversationId, error }) => {
    answered.add(id)
    set(id, { status: RESULT_STATUS.FAILED, conversationId, error: error || 'Unknown error' })
  })

  // never leave an id "moving" if the API didn't mention it
  batchIds
    .filter((id) => !answered.has(id))
    .forEach((id) => set(id, { status: RESULT_STATUS.FAILED, error: 'No result returned' }))

  return next
}

const isRetryable = ({ status, reason, conversationId }) =>
  // a failure carrying a conversation id already has a PDP conversation (only
  // linking it back failed), so sending it again would create a duplicate
  (status === RESULT_STATUS.FAILED && !conversationId) ||
  // never sent because the run was stopped
  (status === RESULT_STATUS.SKIPPED && reason === SKIP_REASONS.STOPPED)

// ids that are safe to send again
export const getRetryableIds = (results) =>
  Object.entries(results)
    .filter(([, result]) => isRetryable(result))
    .map(([id]) => id)

// one flat table, with the run details repeated on every row, so the report
// opens cleanly in a spreadsheet and several reports can be combined
export const REPORT_FIELDS = [
  'id',
  'name',
  'result',
  'reason',
  'conversation_id',
  'warnings',
  'organization_id',
  'organization_name',
  'env',
  'moved_at',
]

export const toReportRows = (audiences, results, { organization, env, movedAt } = {}) =>
  audiences
    .filter(({ _id }) => results[_id])
    .map(({ _id, name }) => {
      const result = results[_id]
      return {
        id: _id,
        name,
        result: result.status,
        reason: result.error || (result.reason ? formatSkipReason(result.reason) : ''),
        conversation_id: result.conversationId || '',
        warnings: (result.warnings || []).join(' | '),
        organization_id: organization?._id || '',
        organization_name: organization?.name || '',
        env: env || '',
        moved_at: movedAt || '',
      }
    })

export function downloadMoveReport({ audiences, results, organization, env, startedAt }) {
  const csv = papa.unparse({
    fields: REPORT_FIELDS,
    data: toReportRows(audiences, results, { organization, env, movedAt: startedAt }),
  })
  const date = startedAt.split('T')[0]
  downloadCSV(csv, `move-to-pdp-report-${env}-${date}`)
}
